import { Component, OnInit, ElementRef } from '@angular/core';
import * as d3 from 'd3';

@Component({
  selector: 'app-gauge-chart',
  template: `<figure id="gauge"></figure>`,
  styles: [`
    .chart-gauge {
      width: 400px;
      margin: 10px auto;
    }
    
    .chart-color1 {
      fill: #DEA82C;
    }

    .chart-color2 {
      fill: #E9621A;
    }

    .chart-color3 {
      fill: #e92213;
    }
    
    .needle,
    .needle-center {
      fill: #464A4F;
    }
    
    .prose {
      text-align: center;
      font-family: sans-serif;
      color: #ababab;
    }
  `]
})
export class GaugeChartComponent {
  constructor(private elementRef: ElementRef) {}

  percent = 0.65;
  barWidth = 40;
  numSections = 3;
  sectionPerc = 1 / this.numSections / 2;
  padRad = 0.05;
  totalPercent: any = 0.75;
  chartInset = 10;

  ngOnInit(): void {
    

    const el = d3.select(this.elementRef.nativeElement.querySelector('figure#gauge'));

    const margin = { top: 20, right: 20, bottom: 30, left: 20 };
    const width = el.node().offsetWidth - margin.left - margin.right;
    const height = width;
    const radius = Math.min(width, height) / 2;

    const percToDeg = (perc: number) => perc * 360;
    const percToRad = (perc: number) => degToRad(percToDeg(perc));
    const degToRad = (deg: number) => deg * Math.PI / 180;

    const svg = el.append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    const chart = svg.append('g')
      .attr('transform', `translate(${(width + margin.left) / 2}, ${(height + margin.top) / 2})`);

    for (let sectionIndx = 1; sectionIndx <= this.numSections; sectionIndx++) {
      const arcStartRad = percToRad(this.totalPercent);
      const arcEndRad = arcStartRad + percToRad(this.sectionPerc);
      this.totalPercent += this.sectionPerc;

      const startPadRad = sectionIndx === 0 ? 0 : this.padRad / 2;
      const endPadRad = sectionIndx === this.numSections ? 0 : this.padRad / 2;

      const arc: any = d3.arc()
        .outerRadius(radius - this.chartInset)
        .innerRadius(radius - this.chartInset - this.barWidth)
        .startAngle(arcStartRad + startPadRad)
        .endAngle(arcEndRad - endPadRad);

      chart.append('path')
        .attr('class', `arc chart-color${sectionIndx}`)
        .attr('d', arc);
    }

    class Needle {
      constructor(private len: number, private radius: number) {}

      drawOn(el: any, perc: number) {
        el.append('circle')
          .attr('class', 'needle-center')
          .attr('cx', 0)
          .attr('cy', 0)
          .attr('r', this.radius);

        el.append('path')
          .attr('class', 'needle')
          .attr('d', this.mkCmd(perc));
      }

      animateOn(el: any, perc: number) {
        const self = this;
        el.transition()
          .delay(500)
          .ease(d3.easeElastic)
          .duration(3000)
          .selectAll('.needle')
          .tween('progress', function() {
            return function(percentOfPercent: number) {
              const progress = 50; // percentOfPercent * perc;
              d3.select('figure#gauge').attr('d', self.mkCmd(progress));
            };
          });
      }

      private mkCmd(perc: number) {
        const thetaRad = this.percToRad(perc) / 2;

        const centerX = 0;
        const centerY = 0;

        const topX = centerX - this.len * Math.cos(thetaRad);
        const topY = centerY - this.len * Math.sin(thetaRad);

        const leftX = centerX - this.radius * Math.cos(thetaRad - Math.PI / 2);
        const leftY = centerY - this.radius * Math.sin(thetaRad - Math.PI / 2);

        const rightX = centerX - this.radius * Math.cos(thetaRad + Math.PI / 2);
        const rightY = centerY - this.radius * Math.sin(thetaRad + Math.PI / 2);

        return `M ${leftX} ${leftY} L ${topX} ${topY} L ${rightX} ${rightY}`;
      }

      private percToRad(perc: number) {
        return degToRad(percToDeg(perc));
      }
    }

    const needle = new Needle(90, 15);
    needle.drawOn(chart, 0);
    needle.animateOn(chart, this.percent);
  }
}
